import React, { useRef, useState } from "react";
import { useAuth } from "./AuthProvider";

const API_BASE = "https://apitigdig61.biti9.ai/api";

export default function UploadNotas() {
  const { account, logout, getToken, expirarSessao } = useAuth();

  const [file, setFile] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | uploading | done | error
  const [resultado, setResultado] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const fileInputRef = useRef(null);

  const isUploading = status === "uploading";

  const handleFileChange = (e) => {
    const selected = e.target.files?.[0];
    if (selected) setFile(selected);
  };

  const handleReset = () => {
    setFile(null);
    setStatus("idle");
    setResultado(null);
    setErrorMsg("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleEnviar = async () => {
    if (!file) {
      setErrorMsg("Selecione um arquivo Excel (.xlsx ou .xls) antes de enviar.");
      return;
    }

    setStatus("uploading");
    setErrorMsg("");
    setResultado(null);

    // Token renovado à parte: se a sessão da Microsoft já caiu, isso volta pra
    // tela de login com o aviso, em vez de cair no catch de erro de upload.
    let token;
    try {
      token = await getToken();
    } catch {
      await expirarSessao("Sua sessão expirou. Faça login novamente.");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("file", file);

      const res = await fetch(`${API_BASE}/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (res.status === 401) {
        await expirarSessao("Sua sessão expirou. Faça login novamente.");
        return;
      }

      const dados = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(dados.error || dados.detail || "Falha ao enviar o arquivo.");
      }

      setResultado(dados);
      setStatus("done");
    } catch (err) {
      setErrorMsg(err.message);
      setStatus("error");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 pt-2 pb-6 px-4">
      <div className="max-w-lg mx-auto flex flex-col items-center justify-start mb-2">
        <img src="logo-tigre.png" alt="logo-tigre" className="w-56" />
      </div>

      <div className="max-w-lg mx-auto bg-white rounded-2xl shadow-md p-8">
        <header className="mb-6 text-center">
          <h1 className="text-2xl font-bold text-slate-800">Envio de Notas para o GISS</h1>
          <p className="text-slate-500 text-sm mt-1">
            Anexe a planilha com as notas fiscais para publicá-las na fila.
          </p>
          {account && (
            <p className="text-xs text-slate-400 mt-2">
              Logado como {account.username}{" "}
              <button onClick={logout} className="underline hover:text-slate-600">
                sair
              </button>
            </p>
          )}
        </header>

        <div className="text-center mb-6">
          <div className="flex flex-col items-center justify-center gap-2">
            <label
              className={`cursor-pointer bg-blue-50 text-blue-700 hover:bg-blue-100 font-medium text-sm px-4 py-2 rounded-lg transition inline-flex items-center gap-2 shadow-sm ${
                isUploading ? "opacity-50 cursor-not-allowed pointer-events-none" : ""
              }`}
            >
              <span>Anexar Arquivo</span>
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                disabled={isUploading}
                className="hidden"
                ref={fileInputRef}
              />
            </label>
            <span className="text-xs text-slate-500 truncate max-w-full">
              {file ? `${file.name} (${(file.size / 1024).toFixed(0)} KB)` : "Nenhum arquivo escolhido"}
            </span>
          </div>
        </div>

        <button
          onClick={handleEnviar}
          disabled={isUploading || !file}
          className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300
                    text-white font-medium py-2.5 rounded-lg transition shadow-sm mb-6"
        >
          {isUploading ? "Enviando..." : "Enviar"}
        </button>

        {errorMsg && (
          <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
            {errorMsg}
          </div>
        )}

        {resultado && (
          <div className="mb-4 text-sm bg-emerald-50 border border-emerald-200 rounded-lg p-3">
            <p className="text-emerald-700 font-medium">
              {resultado.enviados} nota(s) enviada(s) para a fila com sucesso.
            </p>
            {resultado.falhas > 0 && (
              <p className="text-red-600 mt-1">{resultado.falhas} linha(s) com erro.</p>
            )}
            {resultado.erros?.length > 0 && (
              <ul className="mt-2 list-disc list-inside text-red-600">
                {resultado.erros.map((erro, idx) => (
                  <li key={idx}>{erro}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        {resultado?.sem_codigo_servico?.length > 0 && (
          <div className="mb-4 text-sm bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-amber-800 font-medium">
              {resultado.sem_codigo_servico.length} nota(s) descartada(s) por falta de código de serviço:
            </p>
            <div className="mt-2 overflow-x-auto">
              <table className="w-full text-left text-amber-900">
                <thead>
                  <tr className="border-b border-amber-200">
                    <th className="py-1 pr-3 font-medium">Linha</th>
                    <th className="py-1 pr-3 font-medium">Nº Nota</th>
                    <th className="py-1 pr-3 font-medium">Série</th>
                    <th className="py-1 pr-3 font-medium">CNPJ</th>
                    <th className="py-1 pr-3 font-medium">Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {resultado.sem_codigo_servico.map((nota, idx) => (
                    <tr key={idx} className="border-b border-amber-100 last:border-0">
                      <td className="py-1 pr-3">{nota.linha}</td>
                      <td className="py-1 pr-3">{nota.numero_nf}</td>
                      <td className="py-1 pr-3">{nota.serie}</td>
                      <td className="py-1 pr-3">{nota.CNPJ}</td>
                      <td className="py-1 pr-3">{nota.valor}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-amber-700 text-xs mt-2">
              Preencha o código de serviço na planilha e envie essas notas novamente.
            </p>
          </div>
        )}

        {(status === "done" || status === "error") && (
          <button
            onClick={handleReset}
            className="w-full bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium py-2 rounded-lg transition mb-2"
          >
            Enviar outro arquivo
          </button>
        )}

        <button
          onClick={handleReset}
          disabled={isUploading || (!file && status === "idle")}
          className="w-full bg-slate-100 hover:bg-slate-200 disabled:opacity-50
                    disabled:cursor-not-allowed text-slate-700 font-medium py-2
                    rounded-lg transition"
        >
          Limpar tela
        </button>
      </div>

      <img
        src="logo-biti9.png"
        alt="logo-biti9"
        className="fixed bottom-4 right-4 w-24"
      />
    </div>
  );
}
